// Console presentation for a reported GPS accuracy radius.
//
// ONE vocabulary, not two. `management_timeclock_log` grades a punch's `gps_accuracy_meters`
// into high / medium / low with the account's band ceilings (20260829120000, defaults 25 m and
// 100 m from 20260610105628), and the Timeclock report renders those words to operators today.
// Live CAD reuses the same boundaries and the same words, so an officer whose punch reads High
// cannot read Fair thirty seconds later on the map. A second set of bands would be a second
// product claim about the same number.
//
// The ceilings are policy, not Console constants. Callers hand in the thresholds they resolved
// from `get_account_duty_tracking_policy`; when policy is not ready the module fails closed to
// the shipped 25 / 100 pair, the same pair the host uses for an account with no policy row.
//
// Never a coordinate. Product lock Q11 A allows accuracy plus Show on map; a lat/lng dump has no
// representation in this module and no caller that could hand one in.

import {
  DEFAULT_GPS_ACCURACY_BAND_THRESHOLDS,
  GPS_ACCURACY_HIGH_MAX_DEFAULT_METERS,
  GPS_ACCURACY_MEDIUM_MAX_DEFAULT_METERS,
  type GpsAccuracyBandThresholds,
} from "./gps-accuracy-bands";

export type GpsAccuracyBand = "high" | "medium" | "low" | "unavailable";

export type { GpsAccuracyBandThresholds } from "./gps-accuracy-bands";

/**
 * Fallback-only defaults. These are no longer where the boundaries live: the account policy
 * is. They stay exported so the shipped 25 / 100 pair has one Console name, and so a caller
 * with no policy in hand grades exactly like the host grades an account with no policy row.
 */
export const GPS_ACCURACY_HIGH_MAX_METERS = GPS_ACCURACY_HIGH_MAX_DEFAULT_METERS;
export const GPS_ACCURACY_MEDIUM_MAX_METERS = GPS_ACCURACY_MEDIUM_MAX_DEFAULT_METERS;

/** The one line shown when a device reported no usable accuracy. Absence, not a guess. */
export const GPS_ACCURACY_UNAVAILABLE_LINE = "GPS accuracy unavailable";

/**
 * A reported radius is only usable when it is a finite positive number. Null, NaN, and a
 * zero-or-negative reading all mean the same thing to an operator: the device did not tell us
 * how good this fix was. Zero is deliberately unavailable rather than perfect, because no
 * handset measures a position to exactly nothing.
 *
 * `thresholds` are the account ceilings; omit them only when policy is not ready.
 */
export function gpsAccuracyBand(
  meters: number | null | undefined,
  thresholds: GpsAccuracyBandThresholds = DEFAULT_GPS_ACCURACY_BAND_THRESHOLDS,
): GpsAccuracyBand {
  if (typeof meters !== "number" || !Number.isFinite(meters) || meters <= 0) {
    return "unavailable";
  }
  if (meters <= thresholds.highMaxMeters) {
    return "high";
  }
  if (meters <= thresholds.mediumMaxMeters) {
    return "medium";
  }
  return "low";
}

const GPS_ACCURACY_BAND_WORDS: Record<GpsAccuracyBand, string> = {
  high: "High",
  medium: "Medium",
  low: "Low",
  unavailable: "Unavailable",
};

/** Operator-facing word for a band. Same words the Timeclock GPS column already prints. */
export function gpsAccuracyBandLabel(band: GpsAccuracyBand): string {
  return GPS_ACCURACY_BAND_WORDS[band];
}

/**
 * The ready-to-show map line: `GPS ±12 m · High`, or the unavailable sentence.
 *
 * Metres are rounded for operators, with a floor of 1 m. A sub-metre reading rounded to zero
 * would print "±0 m", which claims a perfect fix nobody measured.
 */
export function gpsAccuracyLine(
  meters: number | null | undefined,
  thresholds: GpsAccuracyBandThresholds = DEFAULT_GPS_ACCURACY_BAND_THRESHOLDS,
): string {
  const band = gpsAccuracyBand(meters, thresholds);
  if (band === "unavailable" || typeof meters !== "number") {
    return GPS_ACCURACY_UNAVAILABLE_LINE;
  }
  return `GPS ±${Math.max(1, Math.round(meters))} m · ${gpsAccuracyBandLabel(band)}`;
}
